const marketing = {
  meta: {
    title: "TGReposter — AI-powered Telegram content operations",
    description: "Collect, filter, refine, review, and publish Telegram content from one private workspace.",
  },
  accessibility: {
    home: "TGReposter home",
    mainNavigation: "Main navigation",
    mobileNavigation: "Mobile navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    flowImage: "Abstract content flow moving through AI processing to Telegram destinations",
  },
  nav: {
    salesPlan: "Sales Plan",
    workflow: "How it works",
    capabilities: "Capabilities",
    security: "Security",
    demo: "Request demo",
    dashboard: "Open dashboard",
  },
  hero: {
    badge: "AI-powered Telegram content operations",
    title: "Turn Telegram noise into a publishing signal.",
    body: "Monitor the channels that matter, refine posts with AI, and publish approved content to every Telegram destination from one private workspace.",
    primary: "Request demo access",
    secondary: "See the workflow",
    humanApproved: "Human-approved publishing",
    isolated: "User-isolated workspaces",
    stack: "Built for your stack",
  },
  workflow: {
    eyebrow: "One continuous workflow",
    title: "From source channel to published post.",
    body: "TGReposter keeps collection, filtering, AI enhancement, editorial review, and delivery in one traceable flow—without handing publishing decisions to automation.",
    collect: { title: "Collect", copy: "Watch the public Telegram channels that matter to you." },
    filter: { title: "Filter", copy: "Keep useful posts and remove noise with your own rules." },
    refine: { title: "Refine", copy: "Rewrite, summarize, translate, and create hashtags with AI." },
    publish: { title: "Publish", copy: "Review once, then send to one or many Telegram destinations." },
  },
  inbox: {
    title: "Content Inbox",
    status: "Ready for review",
    matched: "12 matched",
    now: "Now",
    first: { title: "AI infrastructure update", action: "News" },
    second: { title: "Open source model release", action: "Translate" },
    third: { title: "Telegram ecosystem report", action: "Summary" },
  },
  capabilities: {
    eyebrow: "Your editorial control room",
    title: "Automation where it helps. Human judgment where it matters.",
    body: "Use AI to move faster without losing control of your voice. Every post stays editable and reviewable before it reaches your audience.",
    items: {
      privateInbox: "Private content inbox for every user",
      aiProviders: "Gemini and OpenRouter support",
      humanReview: "Human review before publishing",
      multiDestination: "Parallel multi-destination delivery",
      campaigns: "Channel, group, and supergroup campaigns",
      roles: "Role-based team access",
    },
  },
  cards: {
    curation: {
      eyebrow: "Content curation",
      title: "Build a focused feed from the channels you monitor.",
      body: "Collect matching posts, remove unwanted promotions, improve the writing, and publish only what deserves attention.",
    },
    campaign: {
      eyebrow: "Campaign distribution",
      title: "Carry one approved message across your Telegram network.",
      body: "Coordinate delivery to channels, groups, and supergroups while keeping destination results independent.",
    },
  },
  security: {
    eyebrow: "Private by design",
    title: "Your workspace is yours.",
    body: "Sources, filters, inbox posts, destinations, campaigns, and AI preferences are isolated per user. Sensitive credentials stay on the backend, protected by authenticated, role-aware access.",
  },
  salesPlan: {
    "eyebrow": "Sales Plan · Proposed pricing",
    "title": "One platform. A plan for every Telegram operation.",
    "intro": "Start with content curation and scale to multi-channel publishing, campaigns, and teamwork.",
    "billingLabel": "Billing period",
    "periods": {
      "monthly": "Monthly",
      "annual": "Annual"
    },
    "notice": "Approved pricing preview and planned limits. Subscriptions, checkout, and AI top-up purchases are not yet available. Request demo access to discuss a plan.",
    "popular": "Most popular",
    "custom": "Custom",
    "perMonth": "/ month",
    "enterpriseBilling": "Tailored contract",
    "freeForever": "Free forever",
    "monthlyCharge": "Billed monthly",
    "annualCharge": "Billed {{price}} yearly",
    "plannedLimits": "Planned plan limits",
    "metrics": {
      "users": "Team members",
      "sources": "Monitored sources",
      "destinations": "Publishing destinations",
      "campaigns": "Active campaigns",
      "ai": "AI Units / month",
      "history": "Publishing history"
    },
    "history": {
      "days7": "7 days",
      "days90": "90 days",
      "year1": "1 year",
      "year2": "2 years",
      "year3": "3 years",
      "custom": "Custom"
    },
    "request": "Request access",
    "contact": "Talk to our team",
    "discussPlan": "Discuss the {{plan}} plan",
    "annualSavings": "Save up to 17% with annual billing—equivalent to approximately two months free.",
    "plans": {
      "free": {
        "name": "Free",
        "audience": "Get started",
        "description": "Explore the complete content workflow with a small channel setup."
      },
      "creator": {
        "name": "Creator",
        "audience": "Solo creators",
        "description": "Save time curating and publishing across a few channels."
      },
      "professional": {
        "name": "Professional",
        "audience": "Multi-channel operators",
        "description": "Bring monitoring, publishing, and promotion into one workspace."
      },
      "business": {
        "name": "Business",
        "audience": "Media teams",
        "description": "Plan for larger operations with team-level collaboration."
      },
      "agency": {
        "name": "Agency",
        "audience": "Channel networks",
        "description": "Coordinate large publishing networks and campaign portfolios."
      },
      "enterprise": {
        "name": "Enterprise",
        "audience": "Organizations",
        "description": "Discuss bespoke scale, governance, integrations, and support."
      }
    },
    "topUps": {
      "eyebrow": "Optional AI capacity",
      "title": "Add AI Units when you need more.",
      "description": "Planned one-time AI Unit packs complement the monthly allowance included in each tier.",
      "units": "{{units}} AI Units",
      "note": "AI Units reflect underlying model usage, not a fixed number of AI actions. Purchases and commercial AI metering are not live.",
      "packs": {
        "mini": "AI Mini",
        "plus": "AI Plus",
        "pro": "AI Pro",
        "max": "AI Max"
      }
    }
  },
  cta: {
    eyebrow: "See it in your workflow",
    title: "Request demo access.",
    body: "Tell us a little about your Telegram workflow. We’ll review your use case and send the right demo access details.",
    button: "Request demo",
    points: {
      tailored: "A walkthrough tailored to your curation or campaign workflow",
      private: "Your request is handled privately by the TGReposter team",
      human: "No auto-enrollment — demo access is reviewed before it is issued",
    },
  },
  demoForm: {
    eyebrow: "Demo access",
    title: "Tell us where TGReposter should help.",
    fullName: "Your name",
    fullNamePlaceholder: "Jane Smith",
    email: "Email",
    emailPlaceholder: "jane@company.com",
    company: "Company / team (optional)",
    companyPlaceholder: "Your organization",
    telegram: "Telegram username (optional)",
    telegramPlaceholder: "@username",
    useCase: "What do you want to use TGReposter for?",
    useCases: {
      curation: "Content curation and reposting",
      campaigns: "Promotion campaigns",
      both: "Both curation and campaigns",
      other: "Something else",
    },
    message: "What would you like to see in the demo? (optional)",
    messagePlaceholder: "Tell us about your channels, workflow, or the problem you want to solve.",
    submit: "Request demo access",
    submitting: "Sending request…",
    privacy: "We’ll use these details only to review your demo request and contact you about TGReposter.",
    successTitle: "Request received.",
    successBody: "Thanks. We’ll review your use case and follow up with the next step for demo access.",
    submitAnother: "Send another request",
    error: "We couldn’t send your request. Please check your connection and try again.",
  },
  footer: {
    salesPlan: "Sales Plan",
    workflow: "Workflow",
    capabilities: "Capabilities",
    security: "Security",
    demo: "Request demo",
    dashboard: "Dashboard",
  },
} as const;

export default marketing;
